import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Loader2, Plus, Search, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { WahaApiService } from '../../lib/services/wahaApiService';
import type { WahaConfig } from '../../lib/types/whatsappChat';

interface GroupPickerProps {
  /** Cliente del deal: i suoi gruppi già associati vengono mostrati in cima. */
  clientId: string;
  /** JID del gruppo selezionato. Stringa vuota = nessuna selezione. */
  value: string;
  onChange: (jid: string) => void;
  className?: string;
  /** Nasconde la voce "Crea un nuovo gruppo" (quando la scelta nuovo/esistente è esterna). */
  hideCreateNew?: boolean;
}

interface GroupOption {
  jid: string;
  name: string;
}

/**
 * Selettore di un gruppo WhatsApp esistente per il riuso, oppure "crea nuovo".
 * I gruppi vengono caricati dalla config WAHA designata per i gruppi clienti
 * (`settings.whatsapp_group_config_id`) — la stessa che il workflow usa per
 * crearli/gestirli — NON dal singleton WAHA dell'utente corrente.
 * UI con ricerca (la lista può contenere centinaia di gruppi).
 */
export function GroupPicker({ clientId, value, onChange, className, hideCreateNew }: GroupPickerProps) {
  const { settings } = useSettingsStore();
  const groupConfigId = (settings as any)?.whatsapp_group_config_id as string | null | undefined;

  const [loading, setLoading] = useState(true);
  const [clientGroups, setClientGroups] = useState<GroupOption[]>([]);
  const [allGroups, setAllGroups] = useState<GroupOption[]>([]);
  const [noConfig, setNoConfig] = useState(false);
  /** Nome del numero aziendale di cui si elencano i gruppi (WhatsApp mostra solo quelli di cui il numero fa parte). */
  const [configLabel, setConfigLabel] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNoConfig(false);
      try {
        // 1. Gruppi già associati al cliente (non dipendono da WAHA)
        const { data: assigns } = await supabase
          .from('whatsapp_chat_assignments')
          .select('chat_jid, chat_name')
          .eq('client_id', clientId)
          .eq('chat_type', 'group');
        const clientJids = new Set<string>();
        const cGroups: GroupOption[] = (assigns ?? [])
          .filter((a: { chat_jid: string | null }) => !!a.chat_jid)
          .map((a: { chat_jid: string; chat_name: string | null }) => {
            clientJids.add(a.chat_jid);
            return { jid: a.chat_jid, name: a.chat_name || a.chat_jid };
          });

        // 2. Lista completa gruppi dalla config WAHA designata per i gruppi clienti
        let all: GroupOption[] = [];
        if (!groupConfigId) {
          if (!cancelled) setNoConfig(true);
        } else {
          const { data: cfgRow } = await supabase
            .from('api_configurations')
            .select('id, organization_id, configuration, display_name')
            .eq('id', groupConfigId)
            .maybeSingle();
          const conf = (cfgRow as any)?.configuration ?? {};
          if (cfgRow && !cancelled) {
            const cifre = String(conf.phone_number ?? '').split('@')[0].replace(/\D/g, '');
            setConfigLabel((cfgRow as any).display_name || (cifre ? `+${cifre}` : null));
          }
          if (cfgRow && conf.session && conf.api_url) {
            const wahaConfig: WahaConfig = {
              apiUrl: conf.api_url,
              // Chiave non letta dal client: la inietta waha-proxy lato server (07/09/2026).
              apiKey: '',
              session: conf.session,
              phoneNumber: conf.phone_number ?? '',
              organizationId: (cfgRow as any).organization_id ?? '',
              configId: (cfgRow as any).id,
              serviceName: 'waha',
            };
            try {
              const waGroups = await new WahaApiService(wahaConfig).getAllGroups();
              all = waGroups.map((g) => ({ jid: g.id, name: g.name || g.id }));
            } catch {
              // WAHA non raggiungibile: resta la sola lista gruppi del cliente
            }
          }
        }

        if (cancelled) return;
        setClientGroups(cGroups);
        // I gruppi del cliente sono già in cima; rimuovili dalla lista completa per non duplicare
        setAllGroups(all.filter((g) => !clientJids.has(g.jid)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, groupConfigId]);

  const selectedName = useMemo(() => {
    if (!value) return null;
    return (
      clientGroups.find((g) => g.jid === value)?.name ??
      allGroups.find((g) => g.jid === value)?.name ??
      value
    );
  }, [value, clientGroups, allGroups]);

  const q = query.trim().toLowerCase();
  const filteredClient = q ? clientGroups.filter((g) => g.name.toLowerCase().includes(q)) : clientGroups;
  // Niente taglio a 50: con un centinaio di gruppi la lista scorre, e un
  // gruppo che non compare sembra un gruppo che non esiste (visto il 07/09/2026).
  const filteredAll = q ? allGroups.filter((g) => g.name.toLowerCase().includes(q)) : allGroups;

  // Chiuso di default: mostra la selezione, la lista si espande inline al click
  // (spinge il contenuto sotto, niente tendina che finisce fuori dal drawer).
  const [open, setOpen] = useState(false);
  const pick = (jid: string) => {
    onChange(jid);
    setOpen(false);
    setQuery('');
  };

  const rowCls = (active: boolean) =>
    `flex items-center gap-2 w-full px-2.5 py-1.5 text-left text-[12.5px] transition-colors ${
      active ? 'bg-[#2383E2]/10 text-[#2383E2]' : 'text-[#37352F] dark:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.05]'
    }`;

  return (
    <div className={className}>
      {/* Trigger: valore corrente + chevron */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[13px] text-[#37352F] transition-colors hover:bg-[rgba(55,53,47,0.04)] disabled:opacity-60 dark:text-white dark:hover:bg-white/[0.03]"
      >
        {selectedName
          ? <Users className="h-3 w-3 flex-shrink-0 text-[#1FA855]" strokeWidth={1.5} />
          : <Plus className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />}
        <span className="min-w-0 flex-1 truncate">
          {loading ? 'Caricamento gruppi…' : (selectedName ?? 'Crea un nuovo gruppo')}
        </span>
        {loading
          ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-[#9B9A97]" />
          : <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-[rgba(55,53,47,0.45)] transition-transform dark:text-gray-500 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />}
      </button>

      {noConfig && (
        <p className="mt-1 text-[11px] text-[#B7791F]">
          Imposta il "Numero WhatsApp per i gruppi clienti" nelle impostazioni deal per scegliere un gruppo esistente.
        </p>
      )}

      {open && (
        <div className="mt-1 overflow-hidden rounded-md border border-[rgba(55,53,47,0.12)] dark:border-[#454B4E]">
          <div className="relative border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#9B9A97]" strokeWidth={1.5} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca un gruppo…"
              autoFocus
              className="w-full bg-transparent py-1.5 pl-8 pr-3 text-[12.5px] text-[#37352F] outline-none placeholder-[rgba(55,53,47,0.45)] dark:text-white dark:placeholder-gray-500"
            />
          </div>
          <div className="max-h-48 divide-y divide-black/[0.04] overflow-y-auto dark:divide-white/[0.05]">
            {!hideCreateNew && (
              <button type="button" onClick={() => pick('')} className={rowCls(!value)}>
                <Plus className="h-3 w-3 flex-shrink-0" strokeWidth={1.5} />
                <span className="flex-1">Crea un nuovo gruppo</span>
                {!value && <Check className="h-3.5 w-3.5" strokeWidth={1.5} />}
              </button>
            )}

            {filteredClient.length > 0 && (
              <div className="bg-black/[0.02] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#9B9A97] dark:bg-white/[0.03]">
                Gruppi del cliente
              </div>
            )}
            {filteredClient.map((g) => (
              <button key={g.jid} type="button" onClick={() => pick(g.jid)} className={rowCls(value === g.jid)}>
                <Users className="h-3 w-3 flex-shrink-0 text-[#1FA855]" strokeWidth={1.5} />
                <span className="flex-1 truncate">{g.name}</span>
                {value === g.jid && <Check className="h-3.5 w-3.5" strokeWidth={1.5} />}
              </button>
            ))}

            {filteredAll.length > 0 && (
              <div className="bg-black/[0.02] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#9B9A97] dark:bg-white/[0.03]">
                Altri gruppi{configLabel ? ` di ${configLabel}` : ''} · {filteredAll.length}
              </div>
            )}
            {filteredAll.map((g) => (
              <button key={g.jid} type="button" onClick={() => pick(g.jid)} className={rowCls(value === g.jid)}>
                <Users className="h-3 w-3 flex-shrink-0 text-[#9B9A97]" strokeWidth={1.5} />
                <span className="flex-1 truncate">{g.name}</span>
                {value === g.jid && <Check className="h-3.5 w-3.5" strokeWidth={1.5} />}
              </button>
            ))}

            {!loading && filteredClient.length === 0 && filteredAll.length === 0 && q && (
              <div className="px-2.5 py-2.5 text-[12px] text-[#9B9A97]">Nessun gruppo trovato per "{query}".</div>
            )}
          </div>
          {configLabel && (
            <div className="border-t border-[rgba(55,53,47,0.09)] px-2.5 py-1.5 text-[10.5px] leading-snug text-[#9B9A97] dark:border-white/10">
              Compaiono solo i gruppi di cui fa parte il numero {configLabel}, quello che scrive nel gruppo alla firma. Per usarne un altro cambia il numero per i gruppi clienti nelle impostazioni delle offerte.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
